<template>
  <div>
    <!-- email -->
    <div class="customize-section">
      <div class="radio-holder flex items-end gap-2">
        <radio-button
          :label="isProjects ? 'Allowed Email Domain' : 'Email'"
          value="email"
          v-model="pageAccessType.value"
          @change="handlePageAccessTypeChange('email')"
          :isTransparent="true"
          holderStyles="align-items: flex-end"
          customStyles="position: relative; bottom: 2px; font-weight: bold; font-size: 0.853rem"
        />
        <ErrorDisplay
          :error="Boolean(pageAccessType.email.error)"
          :errorMessage="pageAccessType.email.error"
          v-if="pageAccessType.email.error"
        />
      </div>
      <div class="caption">
        <p class="text-left">
          {{
            isProjects
              ? 'Users can sign in with the email associated with the permissible web domain.'
              : ' Users can sign in with their registered email. Select from an allow list or with an allowed domain.'
          }}
        </p>
      </div>
      <template v-if="pageAccessEmailEnabled">
        <div class="email-page-access" v-if="!isProjects">
          <div class="flex items-center gap-1">
            <radio-button
              label="From an Allow List"
              value="email-allow-list"
              v-model="pageAccessType.email.selected"
              @change="handleSelectedTypeChange('email', 'email-allow-list')"
              :isTransparent="true"
              customStyles="position: relative; bottom: 2px; font-weight: bold; font-size: 0.853rem"
            />
            <div class="tool-tip relative -top-1.5">
              <Tooltip
                :popOverContent="allowListTooltip"
                popOverPosition="left"
                popoverWrapperClass="relative md:-left-40 top-8"
              />
            </div>
          </div>
          <div class="caption">
            <p class="text-left">
              Only emails on the list will have permission to access the
              redemption page.
            </p>
          </div>
          <template v-if="pageAccessType.email.type === 'email-allow-list'">
            <div class="allow-list-holder">
              <AllowListRecipientSelect
                :isGroupOrder="true"
                :isMuted="true"
                v-if="true"
              />
            </div>
          </template>

          <div class="margin-top-1">
            <radio-button
              label="With an allowed domain"
              value="email-allowed-domain"
              v-model="pageAccessType.email.selected"
              @change="
                handleSelectedTypeChange('email', 'email-allowed-domain')
              "
              :isTransparent="true"
              customStyles="position: relative; bottom: 2px; font-weight: bold; font-size: 0.853rem"
            />
            <div class="caption">
              <p class="text-left">
                Only emails with the registered web domain will have permission
                to access the redemption page.
              </p>
            </div>
            <template
              v-if="pageAccessType.email.selected === 'email-allowed-domain'"
            >
              <div class="allow-domains is-white">
                <input-tag
                  :class="{ 'updating-tags': updatingDomain }"
                  :validate="validateEmail"
                  class="group-order-domains"
                  placeholder="Enter web domain"
                  v-model="domains"
                  @input="handleUpdateDomains"
                ></input-tag>
              </div>
            </template>
          </div>
        </div>
        <div
          class="email-page-access"
          :class="{ isProjects: isProjects }"
          v-else
        >
          <div class="">
            <template>
              <div class="allow-domains is-white">
                <input-tag
                  :class="{ 'updating-tags': updatingDomain }"
                  :validate="validateEmail"
                  class="group-order-domains"
                  placeholder="Enter web domain"
                  v-model="domains"
                  @input="handleUpdateDomains"
                ></input-tag>
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>

    <!-- group access codes -->
    <div class="customize-section" v-if="!isProjects">
      <div class="radio-holder flex items-end gap-2">
        <radio-button
          label="Group Access Code"
          value="group-access-codes"
          v-model="pageAccessType.value"
          @change="handlePageAccessTypeChange('group-access-codes')"
          :isTransparent="true"
          holderStyles="align-items: flex-end"
          customStyles="position: relative; bottom: 2px; font-weight: bold; font-size: 0.853rem; align-items: flex-end"
        />
        <ErrorDisplay
          :error="Boolean(pageAccessType['group-access-codes'].error)"
          :errorMessage="pageAccessType['group-access-codes'].error"
          v-if="pageAccessType['group-access-codes'].error"
        />
      </div>
      <div class="caption group-access">
        <p class="text-left">
          Users can sign in using a universal code. Set up a multi-use group
          code to permit access to the redemption page.
        </p>

        <template v-if="pageAccessType.value === 'group-access-codes'">
          <div class="group-access-info">
            <small>
              <i
                >Note: For best practices, try an alpha-numeric code with
                special characters.</i
              >
            </small>
          </div>
          <div class="group-access-input-group">
            <input
              type="text"
              v-model="groupAccessCode"
              placeholder="Type a code"
            />
          </div>
        </template>
      </div>
    </div>

    <!-- single sign on -->
    <div style="display: none" class="customize-section">
      <div class="radio-holder flex items-end gap-2">
        <radio-button
          label="Single-Sign-On"
          value="single-sign-on"
          v-model="pageAccessType.value"
          @change="handlePageAccessTypeChange('single-sign-on')"
          :isTransparent="true"
          holderStyles="align-items: flex-end"
          customStyles="position: relative; bottom: 2px; font-weight: bold; font-size: 0.853rem"
        />
        <ErrorDisplay
          :error="Boolean(pageAccessType['single-sign-on'].error)"
          :errorMessage="pageAccessType['single-sign-on'].error"
          v-if="pageAccessType['single-sign-on'].error"
        />
      </div>
      <div class="caption">
        <p class="text-left">
          Users can sign in using secure authentication with a single
          credential. Enable SSO in Settings.
        </p>
      </div>

      <!-- SSO Section -->
      <template v-if="pageAccessType.value === 'single-sign-on'">
        <section class="sso-section">
          <div class="flex items-center gap-2 sso-section__header">
            <div>
              <span class="status" :class="{ enabled: isIDPEnabled }"></span>
            </div>
            <img src="../../../assets/lock.svg" />
            <span v-if="isIDPEnabled" class="sso-status-info"
              >Single Sign-On is enabled.</span
            >
            <span v-else class="sso-status-info"
              >Single Sign-On is disabled. To make changes, go to
              <strong>
                <button @click="handleViewSettings">
                  Settings>Profile>Advanced Settings.
                </button>
              </strong></span
            >
          </div>
        </section>
      </template>
    </div>

    <!-- individual access codes -->
    <div style="display: none" class="customize-section">
      <div class="radio-holder flex items-end gap-2">
        <radio-button
          label="Individual Access Code (To setup, contact account manager)"
          value="individual-access-codes"
          v-model="pageAccessType.value"
          disabled
          @change="handlePageAccessTypeChange('individual-access-codes')"
          :isTransparent="true"
          holderStyles="align-items: flex-end"
          customStyles="position: relative; bottom: 2px; font-weight: bold; font-size: 0.853rem"
        />
        <ErrorDisplay
          :error="Boolean(pageAccessType['individual-access-codes'].error)"
          :errorMessage="pageAccessType['individual-access-codes'].error"
          v-if="pageAccessType['individual-access-codes'].error"
        />
      </div>
      <div class="caption">
        <p class="text-left">
          Users can sign in using a unique code. Generate multi-use individual
          codes to permit access to the
          {{ isProjects ? 'projects' : 'redemption' }} page.
        </p>
        <template v-if="pageAccessType.value === 'individual-access-codes'">
          <GenerateAccessCode
            :modalActive="isAccessCodeModalActive"
            @accessCodeGenerated="
              isAccessCodeGenerated =>
                (accessCodeGenerated = isAccessCodeGenerated)
            "
          />
        </template>
      </div>
    </div>
    <!-- individual access codes -->
  </div>
</template>

<script>
import { mapState } from 'vuex'
import RadioButton from '@/components/Universal/RadioButton.vue'
import InputTag from 'vue-input-tag'
import AllowListRecipientSelect from './AllowListRecipientSelect.vue'
import GenerateAccessCode from './GenerateAccessCode.vue'
import Tooltip from '@/components/Universal/ToolTipInfo.vue'
import ErrorDisplay from './ErrorDisplay.vue'
export default {
  name: 'GroupOrderInfoLoginMethods',
  props: ['isProjects', 'allowed_domains'],
  data() {
    return {
      domains: [], // Renamed to avoid duplicate with prop
      updatingDomain: false,
      accessCodeEnabled: false,
      ssoEnabled: false,
      allowedDomainsEnabled: false,
      allowedListEnabled: false,
      accessCodeGenerated: false,
      groupAccessCode: '',
      pageAccessType: {
        value: '',
        email: {
          selected: false,
          type: '',
          error: ''
        },
        'individual-access-codes': {
          selected: false,
          type: '',
          error: ''
        },
        'group-access-codes': {
          selected: false,
          type: '',
          error: ''
        },
        'single-sign-on': {
          selected: false,
          type: '',
          error: ''
        }
      }
    }
  },

  components: {
    RadioButton,
    InputTag,
    AllowListRecipientSelect,
    GenerateAccessCode,
    Tooltip,
    ErrorDisplay
  },
  methods: {
    handleUpdateDomains(value) {
      this.$emit('onUpdateDomains', value)
    },
    validateEmail(value) {
      // eslint-disable-next-line no-useless-escape
      return /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/.test(
        value
      )
    },
    handlePageAccessTypeChange(type) {
      this.reset()

      this.pageAccessType.value = type
      this.pageAccessType[type].selected = !this.pageAccessType[type].selected
    },
    handleSelectedTypeChange(selectedType, value) {
      this.pageAccessType[selectedType].selected = value
      this.pageAccessType[selectedType].type = value
    },
    handleViewSettings() {
      window.open('/profile/advanced', '_blank')
    },
    reset() {
      for (const key in this.pageAccessType) {
        if (
          this.pageAccessType[key] &&
          typeof this.pageAccessType[key] === 'object' &&
          'selected' in this.pageAccessType[key]
        ) {
          this.pageAccessType[key].error = ''
        }
      }
    },
    validate() {
      this.$emit('onValidate', true)

      this.reset()
      if (!this.pageAccessType.value) {
        this.$emit('onValidate', {
          error: true,
          message: 'No sign-in method selected.'
        })
        return true
      } else {
        let error = false
        if (this.pageAccessType.value === 'email') {
          if (!this.pageAccessType['email'].type) {
            this.pageAccessType['email'].error = 'No email method selected.'
            error = true
            return error
          } else {
            return false
          }
        }
        if (this.pageAccessType.value === 'single-sign-on') {
          if (!this.isIDPEnabled) {
            this.pageAccessType['single-sign-on'].error =
              'Single-Sign-On is disabled.'
            error = true

            return error
          } else {
            return false
          }
        }
        if (this.pageAccessType.value === 'individual-access-codes') {
          if (!this.accessCodeGenerated) {
            this.pageAccessType['individual-access-codes'].error =
              'No access code generated.'
            error = true

            return error
          } else {
            return false
          }
        }
        if (this.pageAccessType.value === 'group-access-codes') {
          if (!this.groupAccessCode) {
            this.pageAccessType['group-access-codes'].error =
              'No access code generated.'
            error = true

            return error
          } else {
            return false
          }
        }
        if (!error) {
          this.$emit('onValidate', error)
        }
      }
    },
    async loadAllowList() {
      await this.$store.dispatch(
        'groupOrder/loadAllowList',
        this.groupOrderDetails.id
      )
    }
  },
  computed: {
    isDetails() {
      return this.$route.fullPath.split('/').includes('details')
    },
    ...mapState({
      loadGroupOrder: state => state.groupOrder.loadGroupOrder,
      groupOrderDetails: state => state.groupOrder.groupOrderDetails,
      isAccessCodeModalActive: state => state.modal.isAccessCodeModalActive,
      isIDPEnabled: state => state.profile.IDPProfile.enabled,
      allowList: state => state.groupOrder.allowList
    }),
    pageAccessEmailEnabled() {
      return this.pageAccessType.value === 'email'
    },
    allowListTooltip() {
      return `<span> <b>Allow List</b> is a list (CSV file) of user emails that are granted access to a Gemnote service.</span> `
    }
  },
  watch: {
    'pageAccessType.value'(newVal) {
      if (newVal) {
        this.$emit('onValidate', true)
        this.reset()
      }
    },
    'pageAccessType.email.type'(newVal) {
      if (newVal) {
        this.$emit('onValidate', true)
        this.reset()
      }
    },
    groupAccessCode(newVal) {
      setTimeout(() => {
        if (newVal) {
          this.$store.dispatch('groupOrder/updateGroupOrder', {
            password: newVal,
            passwordProtected: true
          })
        } else {
          this.$store.dispatch('groupOrder/updateGroupOrder', {
            password: '',
            passwordProtected: false
          })
        }
      }, 1000)
    },
    groupOrderDetails(newval) {
      console.log(this.groupOrderDetails.accessCodesEnabled)
    },
    allowList(newVal) {
      if (this.allowList.length) {
        this.pageAccessType.value = 'email'
        this.pageAccessType.email = {
          selected: 'email-allow-list',
          type: 'email-allow-list'
        }
      }
    },
    allowedDomains(newVal) {
      if (this.allowedDomains.length) {
        this.pageAccessType.value = 'email'
        this.pageAccessType.email = {
          selected: 'email-allowed-domain',
          type: 'email-allowed-domain',
          error: ''
        }
      }
    },
    'groupOrderDetails.accessCodesEnabled?'() {
      if (this.groupOrderDetails['accessCodesEnabled?']) {
        this.pageAccessType.value = 'group-access-codes'
        this.pageAccessType['group-access-codes'] = {
          selected: true,
          type: 'group-access-codes'
        }
      }
    },
    isIDPEnabled(newVal) {
      // if (this.isIDPEnabled) {
      //   this.pageAccessType.value = 'single-sign-on'
      //   this.pageAccessType['single-sign-on'] = {
      //     selected: true,
      //     type: 'single-sign-on',
      //   }
      //   console.log('triggered')
      // }
    }
  },
  async created() {
    if (this.isProjects) {
      this.pageAccessType.value = 'email'
      this.domains = this.allowed_domains
      this.pageAccessType['email'].selected = true
      this.handleSelectedTypeChange('email', 'email-allowed-domain')
    } else {
      await this.loadAllowList()
      if (this.allowList.length) {
        this.pageAccessType.value = 'email'
        this.pageAccessType['email'].selected = true
        this.handleSelectedTypeChange('email', 'email-allow-list')
      } else if (this.allowed_domains.length) {
        this.domains = this.allowed_domains
        this.pageAccessType.value = 'email'
        this.pageAccessType['email'].type = 'email'
        this.pageAccessType['email'].selected = 'email-allowed-domain'
      } else if (this.allowList.length) {
        this.pageAccessType.value = 'email'
        this.pageAccessType['email'].selected = true
        this.handleSelectedTypeChange('email', 'email-allow-list')
      } else if (this.groupOrderDetails['passwordProtected?']) {
        this.pageAccessType.value = 'group-access-codes'
        this.groupAccessCode = this.groupOrderDetails.password
        this.pageAccessType['group-access-codes'] = {
          selected: true,
          type: 'group-access-codes'
        }
      }
      await this.$store.dispatch('profile/getIDPSettings')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/global.scss';

.margin {
  margin-bottom: 1.5rem;
}

.custom-input {
  font-size: 0.813rem !important;
}

.holder {
  text-align: left;

  h3 {
    font-size: 1.063rem;
    font-family: $inter;
    color: $main-6;
    padding-bottom: 2rem;
    padding-top: 0.5rem;

    span {
      font-weight: 400;
    }
  }
}

.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.header-region {
  margin: 2.5rem 0;
  p {
    text-align: center;
    padding-bottom: 0.6rem;
    font-size: 1.3rem;
    font-weight: 400;
  }
}

h4 {
  text-align: left;
}
.info-container {
  background-color: $dark-gray;
  padding-bottom: 1rem;
  margin-left: 2.5rem;
}
.required-info {
  margin: 2rem 0rem;
  padding: 2.5rem 2.5rem 0.5rem 2.5rem;
  display: flex;
  justify-content: space-between;

  .title {
    width: 25%;
    text-align: left;
  }

  .detail {
    width: 72%;
    p {
      font-size: 0.875rem;
      text-align: left;
      margin-bottom: 1.25rem;
    }
  }
}
.domain-container,
.password-container,
.description-container,
.title-container {
  margin: 2rem 0;
  padding: 0 2.5rem 1.75rem 2.5rem;
  display: flex;
  justify-content: space-between;

  .title {
    width: 25%;
    text-align: left;
  }

  .detail {
    width: 72%;
    .subheading {
      margin-bottom: 1rem;
    }
    p {
      text-align: left;
      font-size: 0.875rem;
      line-height: 24px;
    }
  }
}
.aditional-info {
  margin: 2rem 0rem;
  padding: 0 2.5rem;
  display: flex;
  justify-content: space-between;

  .title {
    width: 25%;
    text-align: left;
  }

  .detail {
    width: 72%;
    p {
      font-size: 0.875rem;
      text-align: left;
      margin-bottom: 1.25rem;
    }
  }

  .custom-fields {
    form {
      width: 100%;
      max-width: 14rem;
      text-align: left;
      margin-bottom: 2rem;

      &.padded {
        margin-bottom: 3rem;
      }

      .form-group {
        display: flex;
        align-items: flex-end;
        gap: 1rem;

        &.mt-3 {
          margin-top: 2rem;
        }
      }
    }

    .remove {
      cursor: pointer;
      display: block;
      width: 20px;
      user-select: none;
    }
  }

  .add-field {
    text-align: left;
    margin-bottom: 1.75rem;
    .button-gemnote--small {
      height: 2.375rem;
      text-transform: unset;
      padding: 0px 2rem !important;
      font-weight: normal;
      width: auto;

      &.custom-button {
        background: inherit;
        color: $onyx;
        text-transform: uppercase;
      }
    }
  }
}
.customize-section {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 0px;
  margin-top: 5px;
  margin-bottom: 1rem;
  &.password {
    grid-template-columns: 9.5rem 1fr;
    margin-top: 8px;
  }
  .radio-holder {
    width: 100%;
    height: 28px;
    display: flex;

    &.password {
      margin-top: 0.5rem;
    }
  }

  &.special-instruction {
    grid-template-columns: 100% 1fr !important;
  }

  .sso-section {
    width: 90%;
    margin-top: 1rem;
    margin-left: 1.6rem;
    background-color: #dfdad2;
    padding: 1.2rem 2rem;
    border-radius: 12px;

    .status {
      display: block;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #ce3c3c;

      &.enabled {
        background-color: #68c67e;
      }
    }

    .sso-section__header {
      img {
        width: 24px;
      }
      .sso-status-info {
        text-align: left;
        padding-left: 0.1rem;
        font-size: 0.783rem;
        color: #514d43;
        font-weight: 300;

        strong {
          button {
            font-weight: 700;
          }
        }
      }
    }
  }

  .caption {
    margin-left: 1.6rem;

    p {
      font-size: 0.813rem;
      color: #232220;
    }

    .add-field {
      text-align: left;
      margin-top: 1rem;
      .button-gemnote--small {
        height: 2.375rem;
        text-transform: unset;
        padding: 0px 2rem !important;
        font-weight: normal;
        width: auto;

        &.custom-button {
          background: inherit;
          color: $onyx;
          text-transform: uppercase;
        }
      }

      .view-generated-codes--btn {
        font-size: 0.853rem;
        font-weight: 500;
        text-decoration: underline;
      }
    }
  }
  textarea {
    background: inherit;
    font-family: $nunito;
    padding: 2px 0;
    width: 80%;
    display: block;
    /* margin-left: 0; */
    resize: none;
    border: none;
    border-bottom: 1px solid #24292c;
    font-size: 0.875rem;

    &.custom {
      resize: auto;
    }

    &:focus {
      outline: none;
    }

    &.error {
      border-bottom: 1px solid #da5737;
    }
  }

  .characters-info {
    display: block;
    position: relative;
    top: 0px;
    color: rgba(35, 34, 32, 0.5);
    font-size: 0.625rem;
    width: 80%;
    max-width: 90%;
    text-align: right;
  }
  .password-holder {
    width: 50%;
    position: relative;

    input {
      height: 1.75rem;
      padding: 2px 0;
      font-size: 0.875rem;
      background: inherit;
      font-family: $nunito;
      border: none;
      border-bottom: 1px solid #24292c;

      &.error {
        border-bottom: 1px solid #da5737;
      }
    }

    span {
      position: absolute;
      top: 9px;
      right: 0;
      font-size: 0.625rem;
      color: #676767;
      cursor: pointer;
    }
  }

  ::placeholder {
    color: #9b9b9b;
    font-size: 0.75rem;
  }
  .error-text {
    color: #da5737;
    font-size: 10px;
  }
}
.row {
  display: flex;
  text-align: left;
  flex-wrap: wrap;
  justify-content: space-between;

  .row-holder {
    width: 33.3%;
    &.first {
      width: 25%;
    }
  }
}

.field {
  font-weight: 700;
  margin: 0.5rem 0;
  font-size: 0.813rem;
}

.button-area {
  margin-top: 3rem;
  display: flex;
  justify-content: center;

  button {
    padding: 0 2rem !important;
    width: auto;
    height: 2.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
.domain-content {
  grid-gap: 1rem;
  p {
    margin-top: 0.8rem;
    text-align: left;
  }
}
.allow-domains {
  margin-top: 1.5rem;
  width: 80%;
}

.title p {
  font-size: 0.938rem;
}

.detail p {
  font-size: 0.853rem !important;
}

.email-page-access {
  margin-left: 1.8rem;
  margin-top: 1.2rem;
  .allow-list-holder {
    margin-left: 1.8rem;
    margin-top: 1.2rem;
    width: 80%;
    max-width: 80%;
  }
  .allow-domains {
    margin-left: 1.8rem;
    margin-top: 1rem;
    width: 40%;
    max-width: 40%;
  }

  &.isProjects {
    .allow-domains {
      margin-left: 0rem;
      margin-top: 0rem;
      width: 60%;
      max-width: 60%;
    }
  }
}

.group-access {
  .group-access-info {
    margin-top: 0.5rem;
    text-align: left;
  }
  .group-access-input-group {
    text-align: left;
    margin-top: 1.5rem;
    input {
      width: 50%;
      height: 100%;
    }
  }
}
@media (max-width: 1100px) {
  .required-info {
    flex-direction: column;
    padding: 2rem;
    transition: 1s ease-in;

    .title {
      width: 100%;
      p {
        margin-bottom: 1rem;
      }
    }

    .detail {
      width: 100%;
    }
  }

  .aditional-info {
    flex-direction: column;
    padding: 2rem;
    transition: 1s ease-in;

    .title {
      width: 100%;
      p {
        margin-bottom: 1rem;
      }
    }

    .detail {
      width: 100%;
    }
  }

  .field {
    flex-basis: 50%;
  }

  .row {
    .row-holder {
      width: 100%;
      &.first {
        width: 100%;
      }
    }
  }
}

@media (max-width: 800px) {
  .email-page-access {
    margin-left: 0px;
    .allow-list-holder {
      margin-left: 0px;
      width: 100%;
      max-width: 100%;
    }
  }
  .sso-section__header {
    flex-wrap: wrap;
  }
  .customize-section {
    display: block;
  }
  .custom-block {
    display: block;
  }
  .detail {
    width: 100% !important;
  }
  .domain-container {
    display: block;
  }
  .customize-section .password-holder {
    width: 100%;
  }
}
</style>
